import { GroundRupture, updatePreSpawnEffect, sweepPreSpawnEffects } from "./effects/pre_spawn_effect"
import { stairTravelInRoom, stairTravelPose } from "game-common/stair_travel"
import { RenderPosition, smoothRenderPosition } from "../../../render_position"
import { GlintSubject } from "./effects/dropped_item_glint"
import { EntityVisuals } from "./entity_visuals"
import { renderEntityFrame } from "./entity_frame"
import { advanceProjectile } from "../../../projectile_playback"
import { OTHER_FLOOR_FEATURES, OBSERVATION_FEATURES, FloorRenderFeatures } from "./floor_render_features"
import { Mechanics } from "game-common/mechanics/mechanics"
import { ScalingParams } from "../../../client_util"
import { airborneOverStoreysDepth, initializeEntityDepth } from "./entity_depth"
import { Container, Filter, Graphics, Rectangle, Sprite, Texture, filters } from "pixi.js"
import type { ProjectedComposite, RoofArtwork } from "./roof_composite_overlap"
import { Coordinate, CrossFloorAim, Direction, EmberRosterRole, Entity, Light, LightsContainer, SceneRoomState, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { isBlockWallItem } from "game-common/room/block_walls"
import { companionFloorOffset, insideHouse, TwoStoreyHouse } from "game-common/room/ember/two_storey_house"
import type { ClientRenderable } from "./entity_rendering_manager"
import { WorldOverlayItem, WorldOverlaySurface } from "./world_overlay_surface"
import { ToneEase, VisibilityFade } from "./visibility_fade"
import { isExteriorFloorFall } from "game-common/floor_fall"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { sceneFloorGroundDepth } from "./building_depth"
import { BoundedStoreyContainer, ViewFilteredContainer } from "./view_filtered_container"
import type { DrawnStorey } from "../../../building_levels"
import type { TileEffect } from "game-common/map/map"
import { coverAt, cutMaskRuns, structuresOver } from "./storey_cutaway"
import { publishStoreyVisibility, withdrawStoreyVisibility } from "./storey_visibility"
import { findRendererApp } from "./renderer_app"
import { rectsCover } from "./rendered_tile_occlusion"
import { StoreyFocusFilter } from "./storey_focus_filter"

interface PositionSample { ts: number; x: number; y: number; direction: Direction; angle: number }

/** Deeper storeys fall off the bottom of the view, beneath the nearer ones. */
export const MAX_VISIBLE_FLOORS_BELOW = 4

/** How far above the storey it stands on a storey is raised when its own edge would sort it lower. */
const STOREY_STACK_STEP = 0.001

/**
 * Storeys read by height: those below the viewer recede, darker per storey and outdoors also a touch
 * cooler, the way distance hazes. `above` > 1 lifts the storeys overhead too; off, because from the
 * ground it banded every two-storey facade and cost a filter pass per upper floor. Live: window.storeyTone.
 */
export const STOREY_TONE = { indoors: 0.88, outdoors: 0.82, cool: [0.94, 0.97, 1] as [number, number, number], above: 1, floor: 0.5 }

/**
 * Depth of field: the camera looks straight down, so a storey above the player stands nearer the lens and
 * falls out of focus, softer the higher it is, while the player's own storey stays sharp. `perStorey` is
 * the blur's standard deviation in logical px (an art pixel is two), capped at `max`.
 * `ground`: the overworld far below softens too, from `from` storeys down (`sigma`, then `perStorey` more each,
 * up to `max`); the storeys in between stay sharp.
 * Live: window.storeyFocus (`= false` turns it off).
 */
export const STOREY_FOCUS = { perStorey: 0.64, max: 3.2, ground: { from: 3, sigma: 0.8, perStorey: 0.23, max: 1.03 } }

const storeyFocusConfig = (): typeof STOREY_FOCUS | undefined => {
    if (typeof window === "undefined") return STOREY_FOCUS
    const w = window as any
    if (w.storeyFocus === undefined) w.storeyFocus = STOREY_FOCUS
    return w.storeyFocus && typeof w.storeyFocus === "object" ? { ...STOREY_FOCUS, ...w.storeyFocus } : undefined
}

/** The blur (standard deviation, logical px) of a storey `above` levels over the viewer, if it blurs. */
export const storeyFocusSigma = (above: number, config: typeof STOREY_FOCUS | undefined = storeyFocusConfig()): number | undefined => {
    if (!config || above <= 0) return undefined
    const sigma = Math.min(config.max, above * config.perStorey)
    return sigma > 0 ? sigma : undefined
}

/** The blur (standard deviation, logical px) of the overworld `below` levels under the viewer, if it blurs. */
export const groundFocusSigma = (below: number, config: typeof STOREY_FOCUS | undefined = storeyFocusConfig()): number | undefined => {
    const ground = config?.ground
    if (!ground || below < ground.from) return undefined
    const sigma = Math.min(ground.max, ground.sigma + (below - ground.from) * ground.perStorey)
    return sigma > 0 ? sigma : undefined
}

const storeyToneConfig = (): typeof STOREY_TONE => {
    if (typeof window === "undefined") return STOREY_TONE
    const w = window as any
    if (w.storeyTone === undefined) w.storeyTone = STOREY_TONE
    return w.storeyTone && typeof w.storeyTone === "object" ? { ...STOREY_TONE, ...w.storeyTone } : STOREY_TONE
}

/** The even lift from a storey's tone to its roof's (roofToneGain), also while both ease to a new depth. */
const roofLift = (storey: readonly [number, number, number], roof: readonly [number, number, number] = [1, 1, 1]):
    readonly [number, number, number] => {
    const gain = Math.min(roof[0] / storey[0], roof[1] / storey[1], roof[2] / storey[2])
    return [gain, gain, gain]
}

/**
 * How much to lift a storey's roof through the storey's own tone: the roof stands a storey higher, so
 * it darkens one storey less (none, level with the viewer). Evenly, out of the darkening only: the roof
 * keeps the cool cast, whose lift (most in the red) would push bright thatch past white in the storey's
 * 8-bit buffer and leave its highlights green. Undefined when the storey isn't toned.
 */
export const roofToneGain = (below: number, enclosed: boolean,
    config: typeof STOREY_TONE = storeyToneConfig()): readonly [number, number, number] | undefined => {
    const storey = storeyTone(below, enclosed, config)
    if (!storey) return undefined
    return roofLift(storey, storeyTone(below - 1, enclosed, config))
}

const blockStoreys = new WeakMap<TileMapMetaProvider, { version: number; blocks: boolean }>()
/** Whether a storey has solid blocks (autotile_wall.ts `block`): their tops are the walking surface of the storey above. */
export const storeyHasBlocks = (provider: TileMapMetaProvider | undefined): boolean => {
    // (A storey with no deltas table at all -- a test double -- has no blocks.)
    if (!provider?.getPresentationVersion || !(provider as unknown as { tilemaps?: { deltas?: unknown } }).tilemaps?.deltas) return false
    const version = provider.getPresentationVersion()
    let known = blockStoreys.get(provider)
    if (known?.version !== version) {
        known = { version, blocks: provider.getMapDeltas().some(d => d.layerId === "sprite" && !d.removed && isBlockWallItem(d.context?.itemType)) }
        blockStoreys.set(provider, known)
    }
    return known.blocks
}

/** The [r, g, b] multipliers for a storey `below` levels under the viewer (negative: above), if any. */
export const storeyTone = (below: number, enclosed: boolean,
    config: typeof STOREY_TONE = storeyToneConfig()): readonly [number, number, number] | undefined => {
    if (below < 0) return config.above === 1 ? undefined : [config.above, config.above, config.above]
    if (below === 0) return undefined
    const shade = Math.max(config.floor, Math.pow(enclosed ? config.indoors : config.outdoors, below))
    return enclosed ? [shade, shade, shade] : [shade * config.cool[0], shade * config.cool[1], shade * config.cool[2]]
}

interface SceneMap {
    getCompositeSprites?(): Sprite[]
    getCeilingArtwork?(): RoofArtwork[]
    getTileArt?(x: number, y: number, layerId: string): Sprite[]
    getArtworkVersion?(): number
    isRoofVisibleForPlacement?(): boolean
    overlapsRenderedMap?(bounds: Rectangle): boolean
    collectRenderedTiles?(bounds: Rectangle, out: number[]): void
    collectRenderedSprites?(bounds: Rectangle, out: number[]): void
    setProjectionFloor?(floor: number): void
    setRoofTone?(gain?: readonly [number, number, number]): void
    setViewEntity?(entity: Entity): void
    update(map: TileMapMetaProvider): void
    renderTileMap(): void
    queueMapDelta(deltas: SceneRoomState["mapDeltas"]): void
    flushMapDeltas?(): void
    tileEffect?(effect: TileEffect): void
    updateTileEffects?(): void
    rerender(): void
    destroy(): void
    /** False while this storey has its tiles but has not been drawn with them. */
    hasPainted?(): boolean
}

/** A passive view of the other simulation room, using the normal tile and entity artwork. */
export class SceneFloorRenderer {
    private roomId?: string
    private visuals = new EntityVisuals()
    private overlays?: WorldOverlaySurface
    private spawnEffects = new Map<string, GroundRupture>()
    private completedSpawns = new Set<string>()
    root?: Container
    map?: SceneMap
    provider?: TileMapMetaProvider
    private groundBlur?: StoreyFocusFilter
    private focusBlur?: StoreyFocusFilter
    // What its entities showed last frame, and when that last changed: a blurred storey's snapshot is redrawn then.
    private contentSignature = ""
    private contentChangedAt = 0
    private groundShade?: InstanceType<typeof filters.ColorMatrixFilter>
    private groundTone?: string
    // The storey's tone and its roof's, eased to a new depth as the camera pans to a new level.
    private toneEase = new ToneEase()
    private roofToneEase = new ToneEase()
    // The structures of this storey cut away over the player while the rest stays (storey_cutaway.ts).
    private regionFade = new VisibilityFade()
    private cutRegion?: Set<string>
    private cutRegionVersion = 0
    private cutMask?: Sprite
    private cutMaskKey?: string
    private footprintSource?: TwoStoreyHouse
    private footprintCells: Coordinate[] = []
    private clockOffset?: number
    // This floor's map drew it as the active room until a change of level (SceneRoomRenderer.exchange).
    // The whole map the server sends with the floor's first frame is the one already drawn.
    private adopted = false
    private visibilityFade = new VisibilityFade()
    private entities = new Map<string, { entity: Entity; sprite: ClientRenderable; overlay: WorldOverlayItem;
        samples: PositionSample[]; renderPosition?: RenderPosition; visualDirty?: boolean; bulletTs?: number; bulletStopped?: boolean;
        hoist?: Container }>()

    constructor(private surface: Container, private createMap: (root: Container, sky: Container) => SceneMap,
        private createEntity: (entity: Entity, provider?: TileMapMetaProvider) => ClientRenderable, private observation = false,
        private retainEntity?: (entity: Entity, sprite: ClientRenderable) => boolean,
        private getRole: (id: string) => EmberRosterRole | null = () => null,
        private features: Readonly<FloorRenderFeatures> = observation ? OBSERVATION_FEATURES : OTHER_FLOOR_FEATURES) {}

    clear(): void {
        this.spawnEffects.forEach(effect => effect.destroy())
        this.spawnEffects.clear()
        this.completedSpawns.clear()
        this.entities.forEach(item => {
            const { entity, sprite, overlay } = item
            this.dropHoist(item)
            if (!this.retainEntity?.(entity, sprite)) { sprite.cleanup?.(); sprite.destroy({ children: true }) }
            this.overlays?.remove(entity)
            overlay.destroy({ children: true })
        })
        this.entities.clear()
        this.dropCutMask()
        this.map?.destroy()
        this.root?.parent?.removeChild(this.root)
        this.root?.destroy({ children: true })
        this.groundBlur?.destroy()
        this.groundBlur = undefined
        this.focusBlur?.destroy()
        this.focusBlur = undefined
        this.groundShade?.destroy()
        this.groundShade = undefined
        this.groundTone = undefined
        this.toneEase.reset()
        this.roofToneEase.reset()
        this.cutRegion = undefined
        this.regionFade.reset()
        this.footprintSource = undefined
        this.footprintCells = []
        if (this.roomId && !this.observation) withdrawStoreyVisibility(this.roomId)
        this.map = undefined
        this.root = undefined
        this.overlays = undefined
        this.provider = undefined
        this.roomId = undefined
        this.clockOffset = undefined
        this.visibilityFade.reset()
    }

    update(frames: SceneRoomState[], active?: TwoStoreyHouse): void {
        const frame = frames[0]
        if ((!active && !this.observation) || !frame) { this.clear(); return }
        if (this.roomId !== frame.roomId) this.clear()
        if (!this.root) {
            if (!frame.tilemaps || !frame.tilemapMeta) return
            this.roomId = frame.roomId
            const sky = this.createRoot(frame.tilemaps?.twoStoreyHouse?.floor === 0)
            this.map = this.createMap(this.root, sky)
        }
        const adopted = this.adopted
        this.adopted = false
        if (frame.tilemaps && frame.tilemapMeta && !adopted) {
            // Full snapshots also arrive after roof/floor deletions. Keep the container,
            // fade and entity playback buffers when refreshing the same passive room.
            this.provider = new TileMapMetaProvider(frame.roomId, frame.tilemapMeta, frame.tilemaps, undefined)
            this.map.setProjectionFloor?.(this.observation ? this.provider.twoStoreyHouse?.floor : active.floor)
            this.map.update(this.provider)
            if (this.root.visible || !this.map.flushMapDeltas) this.map.renderTileMap()
        }
        if (!this.root || !this.provider) return
        if (this.observation) active = this.provider.getHousePresentation()
        if (!active) return
        const now = Date.now()
        const timestamp = frame.serverTs ?? now - (this.clockOffset ?? 0)
        const offset = now - timestamp
        this.clockOffset = Math.min(this.clockOffset ?? offset, offset)
        this.root.y = this.observation ? 0 : companionFloorOffset(active, this.provider.twoStoreyHouse)
        const floor = this.provider.twoStoreyHouse.floor
        this.root.zIndex = this.floorZIndex(active)
        const blur = this.heightBlur(floor, active.floor)
        this.setBackgroundFilters(blur, this.groundShade && this.root.filters?.includes(this.groundShade)
            ? this.groundShade : undefined)
        if (frame.mapDeltas?.length) {
            this.map.queueMapDelta(frame.mapDeltas)
            this.map.rerender()
            if (this.root.visible || !this.map.flushMapDeltas) this.map.renderTileMap()
            else this.map.flushMapDeltas()
        }
        const present = new Set<string>()
        for (const message of frame.messages) {
            if (!message.position || message.entityType === "system") continue
            if (message.entityType === "bullet" && message.entityState === "dead") continue
            present.add(message.entityId)
            let item = this.entities.get(message.entityId)
            if (!item) {
                const entity = new Entity()
                Object.assign(entity, message)
                entity.location = Object.assign({ x: message.position[0], y: message.position[1] }, { roomId: frame.roomId })
                const sprite = this.createEntity(entity, this.provider)
                initializeEntityDepth(entity, sprite, this.provider)
                const overlay = this.overlays.offer(entity)
                sprite.setWorldOverlayItem(overlay)
                sprite.interactive = false
                sprite.interactiveChildren = false
                this.root.addChild(sprite)
                item = { entity, sprite, overlay, samples: [],
                    bulletTs: entity.entityType === "bullet" ? now : undefined }
                this.entities.set(message.entityId, item)
            }
            const { entity, sprite } = item
            // Stair playback uses current phase coordinates, so old delayed samples
            // must not pull the actor back toward the opening when the walk finishes.
            if (message.stairTravel || entity.stairTravel) {
                item.samples.length = 0
                item.renderPosition = undefined
            }
            Object.assign(entity, message)
            entity.floorFall = message.floorFall
            entity.stairTravel = message.stairTravel
            if (entity.entityType !== "bullet") {
                // Keep authoritative positions off the drawn entity between snapshots.
                const previous = item.samples[item.samples.length - 1]
                const sample = { ts: timestamp, x: message.position[0], y: message.position[1],
                    direction: message.direction, angle: message.angle }
                // Idle companion snapshots are suppressed between heartbeats. When
                // movement resumes, the prior position was still current at the last
                // 100ms server check; do not interpolate over the whole quiet period.
                if (!this.observation && previous && timestamp - previous.ts > 250) {
                    item.samples.splice(0, item.samples.length - 1)
                    previous.ts = timestamp - 100
                }
                if (previous && Math.hypot(sample.x - previous.x, sample.y - previous.y) > 4 * TileSize) {
                    item.samples.length = 0
                    item.renderPosition = undefined
                    entity.location = { x: sample.x, y: sample.y }
                }
                if (item.samples.length && item.samples[item.samples.length - 1].ts >= timestamp) item.samples.pop()
                item.samples.push(sample)
                if (item.samples.length > 32) item.samples.shift()
            }
            entity.movement.direction = message.direction
            entity.movement.angle = message.angle
            entity.roomId = frame.roomId
            entity.maxHp = message.hpMax
            item.visualDirty = true
            if (this.root.visible) {
                this.visuals.update(sprite, entity, this.features)
                item.visualDirty = false
            }
            sprite.visible = message.visible !== false && !item.bulletStopped
            const subject = sprite as ClientRenderable & Partial<GlintSubject>
            if (entity.npcType === "collectable" && subject.glintArt)
                this.overlays.offerDroppedItemGlint(entity.entityId, subject as GlintSubject)
            if (this.root.visible) this.overlays.updateEntity(entity)

        }
        this.entities.forEach((item, id) => {
            if (present.has(id)) return
            this.spawnEffects.get(id)?.destroy()
            this.spawnEffects.delete(id)
            this.completedSpawns.delete(id)
            this.dropHoist(item)
            // A mounting body is about to arrive on another floor. Hand its existing artwork
            // over before creating the destination body; rebuilding it would briefly draw nothing.
            if (!this.retainEntity?.(item.entity, item.sprite)) {
                item.sprite.parent?.removeChild(item.sprite)
                item.sprite.cleanup?.()
                item.sprite.destroy({ children: true })
            }
            this.overlays.remove(item.entity)
            item.overlay.destroy({ children: true })
            this.entities.delete(id)
        })
    }

    /**
     * A body of this storey in the air between storeys -- jumping up out of it, coming down into it -- is drawn in front of
     * the viewer's storey's art where nothing stands in front of it (entity_depth.ts airborneOverStoreysDepth), which this
     * storey's layer, drawn as one, cannot be: so it is carried out of the layer into a container of its own, placed and
     * toned as the layer is, sorted with the viewer's storey. Back in the layer once it is down or up.
     */
    private hoistAirborne(item: { entity: Entity; sprite: ClientRenderable; hoist?: Container }, active: TwoStoreyHouse, visible: boolean): void {
        const depth = visible && !this.observation && this.root.alpha > 0.5
            ? airborneOverStoreysDepth(item.entity, this.viewerProvider(active), this.root.y) : undefined
        if (depth === undefined) { this.dropHoist(item); return }
        if (!item.hoist) {
            item.hoist = new Container()
            item.hoist.interactive = false
            item.hoist.interactiveChildren = false
            this.surface.addChild(item.hoist)
        }
        if (item.sprite.parent !== item.hoist) item.hoist.addChild(item.sprite)
        item.hoist.position.set(this.root.x, this.root.y)
        item.hoist.zIndex = depth
        item.hoist.alpha = this.root.alpha
        // Toned as its storey is; not blurred (the storey's focus filter keeps its own snapshot of the storey).
        item.hoist.filters = this.groundShade && this.root.filters?.includes(this.groundShade) ? [this.groundShade] : null
    }

    private dropHoist(item: { sprite: ClientRenderable; hoist?: Container }): void {
        if (!item.hoist) return
        if (item.sprite.parent === item.hoist && this.root) this.root.addChild(item.sprite)
        item.hoist.parent?.removeChild(item.hoist)
        item.hoist.filters = null
        item.hoist.destroy()
        item.hoist = undefined
    }

    /** The viewer's storey's map, through the storeys linked to this one's (SceneRoomRenderer.linkFloorSupport). */
    private viewerProvider(active: TwoStoreyHouse): TileMapMetaProvider | undefined {
        const seen = new Set<TileMapMetaProvider>()
        let p = this.provider
        while (p?.twoStoreyHouse && p.twoStoreyHouse.floor !== active.floor && !seen.has(p)) {
            seen.add(p)
            p = p.twoStoreyHouse.floor < active.floor ? p.getUpperFloorProvider() : p.getFloorSupportProvider()
        }
        return p?.twoStoreyHouse?.floor === active.floor ? p : undefined
    }

    /** The floor's root, with its sky layer above everything in it, added to the scene. Returns the sky. */
    createRoot(ground: boolean): Container {
        // The ground room contains the whole forest. Small upper rooms keep their
        // artwork bounds so their filters do not allocate a full viewport texture.
        this.root = ground ? new ViewFilteredContainer() : new BoundedStoreyContainer()
        this.root.sortableChildren = true
        this.root.interactiveChildren = false
        const sky = new Container()
        sky.zIndex = 1000000
        this.root.addChild(sky)
        this.overlays = new WorldOverlaySurface(undefined, this.root, this.features)
        this.overlays.scale.set(1 / ScalingParams.surfaceZoom)
        this.overlays.zIndex = 2000000
        // Nameplates and health bars stay legible on a storey seen through either height blur.
        Object.assign(this.overlays, { heightBlurExempt: true, focusBlurExempt: true })
        this.root.addChild(this.overlays)
        this.surface.addChild(this.root)
        return sky
    }

    /** Draw this floor with a map that already shows it (see SceneRoomRenderer.exchange). */
    adopt(provider: TileMapMetaProvider, map: SceneMap): void {
        this.roomId = provider.roomId
        this.provider = provider
        this.map = map
        this.adopted = true
        // It was the viewer's own storey, drawn untoned: it recedes from there.
        this.toneEase.reset([1, 1, 1])
        this.roofToneEase.reset([1, 1, 1])
    }

    /** Give up the map, without destroying what it drew, ahead of clear(). */
    releaseMap(): SceneMap | undefined {
        const map = this.map
        this.map = undefined
        return map
    }

    private backgroundBlur(floor: number, activeFloor: number): StoreyFocusFilter | undefined {
        // Only the overworld softens; intermediate storeys stay sharp. Each blurred floor is a
        // full-view filter pass, so blurring every storey made each climb cost frames.
        const sigma = this.observation || floor !== 0 ? undefined : groundFocusSigma(activeFloor - floor)
        if (!sigma) return undefined
        // The blur of a storey out of focus (StoreyFocusFilter), anchored to the forest's art by its container
        // (ViewFilteredContainer). Its stairs and ladders, the way up, stay sharp.
        this.groundBlur ||= Object.assign(new StoreyFocusFilter(), { exempt: "heightBlurExempt" as const })
        this.groundBlur.focus(sigma, findRendererApp()?.renderer.resolution ?? 1)
        return this.groundBlur
    }

    /** The floor's height blur: the overworld far below, or a storey above the player out of focus. */
    private heightBlur(floor: number, activeFloor: number): Filter | undefined {
        return this.backgroundBlur(floor, activeFloor) ?? this.foregroundBlur(floor, activeFloor)
    }

    private foregroundBlur(floor: number, activeFloor: number): StoreyFocusFilter | undefined {
        const sigma = this.observation ? undefined : storeyFocusSigma(floor - activeFloor)
        if (!sigma) return undefined
        this.focusBlur ||= new StoreyFocusFilter()
        this.focusBlur.focus(sigma, findRendererApp()?.renderer.resolution ?? 1)
        this.focusBlur.contentVersion = String(this.map?.getArtworkVersion?.() ?? 0)
        this.focusBlur.contentChangedAt = this.contentChangedAt
        return this.focusBlur
    }

    /**
     * The shade comes first and the blur last, so the blur's final pass lays the storey on the screen (snapped to its
     * pixels, StoreyFocusFilter); last behind the blur, the shade would copy the blur's coarse texture there, nearest.
     */
    private setBackgroundFilters(blur?: Filter, shade?: InstanceType<typeof filters.ColorMatrixFilter>): void {
        // pixi draws a filtered storey at its filters' lowest resolution, and a filter's own is 1 unless set. A shade at
        // 1 on a 2.5x screen took the storey's nearest-sampled art at 1 texel a logical px, on the screen's grid, and
        // stretched it back nearest: every storey below the player crawled as the camera moved. At the screen's own,
        // shaded art is the art; under a blur the shade pass is worked at the blur's resolution.
        if (shade) shade.resolution = findRendererApp()?.renderer.resolution ?? 1
        const current = this.root.filters
        const count = Number(!!blur) + Number(!!shade)
        if ((current?.length ?? 0) === count && (!shade || current[0] === shade) &&
            (!blur || current[count - 1] === blur)) return
        this.root.filters = shade ? blur ? [shade, blur] : [shade] : blur ? [blur] : null
    }

    private floorZIndex(active: TwoStoreyHouse): number {
        const house = this.provider.getHousePresentation()
        const floor = house.floor
        if (floor < active.floor) return -1000 + floor
        // A recessed or edited storey must sort at its own live southern edge.
        // Borrowing the active floor's extent puts northern upper tiles over
        // trees whose trunks stand in front of that storey.
        return sceneFloorGroundDepth(house.top + house.depth - 1, floor)
    }

    /** Whether anything on this storey moved or changed its look since last frame (see contentChangedAt). */
    private noteContentChange(now: number): void {
        let signature = ""
        this.entities.forEach(({ entity, sprite }) => {
            signature += `${entity.entityId}:${sprite.visible ? 1 : 0}:${Math.round(entity.location.x * 2)},${Math.round(entity.location.y * 2)}:` +
                `${entity.movement.direction}:${entity.entityState}:${entity.hp};`
        })
        if (signature === this.contentSignature) return
        this.contentSignature = signature
        this.contentChangedAt = now
    }

    getEntity(id: string): Entity | undefined { return this.entities.get(id)?.entity }
    /** The active floor's arrival may beat the throttled source-floor removal snapshot. */
    handoffMount(id: string, destinationRoomId: string): boolean {
        const item = this.entities.get(id)
        if (!item?.entity.animationPose?.mount || this.roomId === destinationRoomId ||
            !this.retainEntity?.(item.entity, item.sprite)) return false
        // Left behind, the emptied hoist kept drawing through this storey's tone filter after clear() destroyed it.
        this.dropHoist(item)
        this.entities.delete(id)
        this.spawnEffects.get(id)?.destroy()
        this.spawnEffects.delete(id)
        this.completedSpawns.delete(id)
        this.overlays.remove(item.entity)
        item.overlay.destroy({ children: true })
        return true
    }

    getRenderable(id: string): ClientRenderable | undefined { return this.entities.get(id)?.sprite }

    /** Part of this storey is cut away over the player: it no longer covers them there. */
    get cutting(): boolean { return !!this.cutRegion && this.regionFade.alpha < 0.5 }

    /** The cells this storey draws on: its floor, walls and roofs. */
    // What stands over the player's cover, kept while the footprint and the cover are the same objects (both are cached):
    // splitting the footprint into pieces every frame cost ~0.5ms a frame per storey indoors.
    private structuresOverMemo?: { footprint: readonly Coordinate[]; cover: ReadonlySet<string>; over: ReturnType<typeof structuresOver> }

    private structuresOver(footprint: readonly Coordinate[], cover: ReadonlySet<string>): ReturnType<typeof structuresOver> {
        const memo = this.structuresOverMemo
        if (memo?.footprint === footprint && memo.cover === cover) return memo.over
        const over = structuresOver(footprint, cover)
        this.structuresOverMemo = { footprint, cover, over }
        return over
    }

    private footprint(): Coordinate[] {
        const house = this.provider?.getHousePresentation()
        if (house !== this.footprintSource) {
            this.footprintSource = house
            this.footprintCells = house ? [...house.floorCells, ...house.wallCells, ...house.roofCells] : []
        }
        return this.footprintCells
    }

    private sameRegion(cells: Set<string>): boolean {
        const current = this.cutRegion
        if (!current || current.size !== cells.size) return false
        for (const cell of cells) if (!current.has(cell)) return false
        return true
    }

    /**
     * The cut region faded out through a soft mask over the storey: opaque everywhere, the region (and the
     * art it reaches up into) at the region's fade. Baked small and smoothed by scaling, re-baked only while
     * the fade moves; no mask at all once the region is back.
     */
    private updateCutMask(): void {
        const region = this.cutRegion, alpha = this.regionFade.alpha
        const renderer = findRendererApp()?.renderer
        if (!region || alpha >= 1 || !this.root?.visible || !this.provider || !renderer) { this.dropCutMask(); return }
        const level = Math.round(alpha * 24) / 24
        const maskKey = `${this.cutRegionVersion}:${level}`
        if (maskKey === this.cutMaskKey && this.root.mask === this.cutMask) return
        this.cutMaskKey = maskKey
        const margin = 4
        const left = -margin, top = -margin, right = this.provider.width() + margin, bottom = this.provider.height() + margin
        const shape = new Graphics()
        for (const run of cutMaskRuns(region, level, left, top, right, bottom)) {
            if (run.alpha <= 0) continue
            shape.beginFill(0xffffff, run.alpha)
                .drawRect(run.x0 * TileSize, run.y * TileSize, (run.x1 - run.x0) * TileSize, TileSize).endFill()
        }
        const bounds = new Rectangle(left * TileSize, top * TileSize, (right - left) * TileSize, (bottom - top) * TileSize)
        const texture = renderer.generateTexture(shape, { region: bounds, resolution: 1 / 8 })
        shape.destroy()
        if (!this.cutMask || this.cutMask.destroyed) {
            this.cutMask = new Sprite()
            this.root.addChild(this.cutMask)
        }
        const previous = this.cutMask.texture
        this.cutMask.texture = texture
        this.cutMask.position.set(bounds.x, bounds.y)
        if (previous !== Texture.EMPTY) previous.destroy(true)
        this.root.mask = this.cutMask
    }

    private dropCutMask(): void {
        const mask = this.cutMask
        if (this.root && !this.root.destroyed && this.root.mask === mask) this.root.mask = null
        this.cutMask = undefined
        this.cutMaskKey = undefined
        if (!mask) return
        const texture = mask.texture
        if (!mask.destroyed) {
            mask.parent?.removeChild(mask)
            mask.destroy()
        }
        if (texture && texture !== Texture.EMPTY) texture.destroy(true)
    }

    getLights(): LightsContainer {
        const lights = new LightsContainer()
        this.entities.forEach(({ entity }) => {
            if ((entity.lightMeta || entity.npcType === "light") && entity.visible !== false && !entity.isDead()) lights.set(entity.entityId,
                { entityId: entity.entityId, location: entity.location,
                    radius: entity.lightRadius(), intensity: entity.lightIntensity() })
        })
        return lights
    }

    /** A crumble, shake or repair on this storey plays on its own map, so it shows where the storey is drawn. */
    tileEffect(effect: TileEffect): void {
        if (!this.map || !this.provider) return
        if (effect.hp !== undefined && effect.hpMax !== undefined && (effect.effectType === "damage" || effect.effectType === "repair")) {
            this.provider.setMapDeltaHealth(effect.mapRoughCoordinates, effect.hp, effect.hpMax, effect.layerId)
        }
        this.map.tileEffect?.(effect)
    }

    projectileAim(point: Coordinate, active: TwoStoreyHouse): CrossFloorAim | undefined {
        if (!this.root?.visible || this.root.alpha <= .15 || !this.provider ||
            this.provider.twoStoreyHouse.floor === active.floor) return
        const localY = point.y - this.root.y
        // Only run on a trigger pull; passive scenes remain non-interactive.
        for (const { entity, sprite } of this.entities.values()) {
            if (!sprite.visible || entity.visible === false || entity.isDead() || entity.floorFall || entity.stairTravel ||
                !["player", "npc"].includes(entity.entityType) || !entity.strikeable) continue
            const bounds = entity.getBounds()
            // All LPC bodies share the same sole line, including small creatures whose
            // visible bodies sit below their simulation centre. Centre-based bounds miss
            // their feet and silently turn a downstairs shot into a same-floor shot.
            // Humanoids additionally need their full 25x50 body rather than 25x25 bounds.
            const humanoid = Mechanics.entity.body.isHumanoid(entity.appearance?.race)
            const body = entity.appearance?.race
                ? humanoid ? Mechanics.entity.body.dimensions(entity) : entity.dimensions
                : undefined
            const scale = Number.isFinite(entity.spriteScale) && entity.spriteScale > 0 ? entity.spriteScale : 1
            const x1 = body ? entity.location.x - body.w * scale / 2 : bounds.x1
            const x2 = body ? entity.location.x + body.w * scale / 2 : bounds.x2
            const y2 = body ? entity.location.y + BODY_GROUND_OFFSET : bounds.y2
            const y1 = body ? y2 - body.h * scale : bounds.y1
            if (point.x >= x1 && point.x <= x2 && localY >= y1 && localY <= y2 &&
                !this.provider.isUnderRoofAt(entity.location))
                return { roomId: this.roomId, x: entity.location.x, y: entity.location.y }
        }
    }

    /** `cover`: the covered area the player stands in (storey_cutaway.ts coverAt), shared by every floor. */
    /** `prepaint`: draw this storey even while hidden, if it never has been (the room-load curtain is down). */
    render(active: TwoStoreyHouse | undefined, player: Entity, cover?: ReadonlySet<string>, prepaint = false): void {
        if (this.observation) active = this.provider?.getHousePresentation()
        if (!this.root || !active || !player?.location) return
        if (this.observation) this.map?.setViewEntity?.(player)
        this.root.y = this.observation ? 0 : companionFloorOffset(active, this.provider.twoStoreyHouse)
        const floor = this.provider.twoStoreyHouse.floor
        this.root.zIndex = this.floorZIndex(active)
        const enclosed = !isExteriorFloorFall(player.floorFall) && insideHouse(active, player.location)
        // The overworld is the backdrop around the tower, visible at any height; deeper storeys
        // still supply collision and floor support while hidden.
        const inView = floor === 0 || active.floor - floor <= MAX_VISIBLE_FLOORS_BELOW
        // Over a player indoors, cut away only this storey's structures standing over them, unless that is all of it.
        const over = !this.observation && floor > active.floor && enclosed && cover
            ? this.structuresOver(this.footprint(), cover) : undefined
        const regional = !!over && !over.whole
        if (regional && over.cut.size) {
            if (!this.sameRegion(over.cut)) { this.cutRegion = over.cut; this.cutRegionVersion++ }
        }
        this.visibilityFade.update(this.observation || (floor < active.floor ? inView : !enclosed || regional))
        this.regionFade.update(!(regional && over.cut.size))
        if (this.regionFade.alpha >= 1 && !(regional && over.cut.size)) this.cutRegion = undefined
        this.root.alpha = this.visibilityFade.alpha
        this.root.visible = this.visibilityFade.visible
        const visible = this.root.visible
        this.updateCutMask()
        if (!this.observation) publishStoreyVisibility(this.roomId, visible ? this.root.alpha : 0,
            this.cutRegion, this.regionFade.alpha)
        const blur = visible ? this.heightBlur(floor, active.floor) : undefined
        // Standing on the tops of the blocks of the storey just below, the viewer walks on that storey's art: it does not
        // recede, and the storeys under it recede a storey less.
        let under: TileMapMetaProvider | undefined = this.provider
        for (let k = floor; under && k < active.floor - 1; k++) under = under.getUpperFloorProvider()
        const depth = active.floor - floor - (floor < active.floor && storeyHasBlocks(under) ? 1 : 0)
        // Eased, hidden or not, so a storey coming into view takes up its tone where it stands.
        const target = this.observation ? undefined : storeyTone(depth, enclosed)
        const eased = this.toneEase.update(target)
        const roof = this.roofToneEase.update(target && storeyTone(depth - 1, enclosed))
        const tone = visible ? eased : undefined
        if (tone) {
            this.groundShade ||= new filters.ColorMatrixFilter()
            // A plain per-channel scale: restored copies of this floor's art take it as a tint (roof_composite_overlap.ts).
            const key = tone.join(",")
            if (key !== this.groundTone) {
                const [r, g, b] = tone
                this.groundShade.matrix = [r, 0, 0, 0, 0, 0, g, 0, 0, 0, 0, 0, b, 0, 0, 0, 0, 0, 1, 0]
                this.groundTone = key
            }
        }
        this.setBackgroundFilters(blur, tone ? this.groundShade : undefined)
        this.map?.setRoofTone?.(tone ? roofLift(tone, roof) : undefined)
        this.map?.setProjectionFloor?.(active.floor)
        // Hidden maps still supply collision and floor support to visible neighbours.
        // Keep deltas applied, while leaving the requested paint pending until reveal -- except a storey never drawn
        // while the room-load curtain is down: drawn behind it, it costs nothing when it is first revealed (walking out
        // from under a roof drew the castle's upper storeys all in one frame, ~40ms).
        if (visible || !this.map?.flushMapDeltas || (prepaint && this.map.hasPainted?.() === false)) this.map?.renderTileMap()
        else this.map.flushMapDeltas()
        this.map?.updateTileEffects?.()
        // Match the active room's playback delay, covering the companion's 100ms snapshot gap.
        const timestamp = Date.now() - (this.clockOffset ?? 0) - 150
        const now = Date.now()
        this.entities.forEach(item => {
            const { entity, sprite, overlay, samples } = item
            if (entity.entityType === "bullet") {
                // Match active-floor bullets: integrate every frame from the first
                // received position, rather than waiting on delayed snapshots. Keep
                // a locally finished bullet hidden until its authoritative removal.
                const dt = Math.max(0, now - (item.bulletTs ?? now)) / 1000
                item.bulletTs = now
                if (!item.bulletStopped && (entity.speed2.x || entity.speed2.y))
                    item.bulletStopped = !advanceProjectile(entity, dt, this.provider)
                sprite.visible = entity.visible !== false && !item.bulletStopped
            } else {
                while (samples.length >= 2 && samples[1].ts <= timestamp) samples.shift()
                const from = samples[0]
                if (!from) return
                const to = samples[1]
                const progress = to && timestamp >= from.ts
                    ? Math.min(1, (timestamp - from.ts) / (to.ts - from.ts)) : 0
                // NPC brains move in discrete turns, so snapshots can repeat a position before
                // jumping ahead. Match active-floor smoothing to bridge those short pauses.
                const x = from.x + ((to?.x ?? from.x) - from.x) * progress
                const y = from.y + ((to?.y ?? from.y) - from.y) * progress
                if (visible) {
                    item.renderPosition = smoothRenderPosition(item.renderPosition, x, y, now)
                    entity.location.x = item.renderPosition.x
                    entity.location.y = item.renderPosition.y
                } else {
                    entity.location.x = x
                    entity.location.y = y
                }
                const facing = progress > 0 ? to : from
                entity.movement.direction = facing.direction
                entity.movement.angle = facing.angle
            }
            if (!visible) {
                // Resume from the current playback position instead of easing from a
                // stale hidden sprite. Projectiles and vertical travel keep stepping.
                item.renderPosition = undefined
                if (entity.entityType !== "bullet" && !entity.stairTravel && !entity.floorFall) return
            }
            if (visible && item.visualDirty) {
                this.visuals.update(sprite, entity, this.features, { role: this.getRole(entity.entityId) })
                item.visualDirty = false
            }
            // Roster updates can arrive without a new room snapshot.
            if (visible) this.visuals.updateRole(sprite, this.getRole(entity.entityId))
            // The local player's two halves share predicted input, not two network clocks.
            if (entity.stairTravel?.sourceEntityId === player.entityId && entity.stairTravel?.projection) {
                const view = stairTravelInRoom(player.stairTravel, this.roomId)
                if (!view || player.roomId === this.roomId) {
                    sprite.visible = overlay.visible = false
                    return
                }
                entity.stairTravel = { ...view, sourceEntityId: player.entityId }
                entity.location = { ...player.location, ...stairTravelPose(view, 0).location }
                sprite.visible = entity.visible !== false
            }
            renderEntityFrame(entity, sprite, this.roomId, visible, now, this.provider)
            this.hoistAirborne(item, active, visible)
            updatePreSpawnEffect(entity, sprite, this.root, this.spawnEffects, this.completedSpawns)
            this.overlays.updateEntity(entity)
            overlay.visible = sprite.visible && !entity.stairTravel?.projection

        })
        sweepPreSpawnEffects(this.spawnEffects, this.completedSpawns, id => visible && this.entities.get(id)?.sprite.visible)
        if (visible) this.noteContentChange(now)
        if (visible) {
            this.overlays.animateDroppedItemGlints(now)
            this.overlays.animateBonecallerWarnings()
        }
    }
}

/** Each passive floor retains its own artwork and movement buffer. */
export class SceneRoomRenderer {
    private floors = new Map<string, SceneFloorRenderer>()
    private backgroundArtwork = { key: "", composites: [] as ProjectedComposite[], ceilings: [] as RoofArtwork[] }
    // The first companion remains accessible for map previews and diagnostics.
    get root(): Container | undefined { return this.floors.values().next().value?.root }
    get map(): SceneMap | undefined { return this.floors.values().next().value?.map }

    /**
     * The art of the storeys below the active one, in the active surface's pixels: their composites (trees),
     * which may stand in front of the storeys above them, and the walls and roofs of every storey but the
     * ground, which a lower storey's tree can stand in front of.
     */
    backgroundCompositeArtwork(active?: TwoStoreyHouse): { key: string; composites: ProjectedComposite[]; ceilings: RoofArtwork[] } {
        const versions: string[] = []
        for (const [id, floor] of this.floors) {
            if (!active || !floor.root || floor.provider?.twoStoreyHouse?.floor >= active.floor) continue
            versions.push(`${id}:${floor.map?.getArtworkVersion?.()}:${floor.root.x}:${floor.root.y}`)
        }
        const key = versions.join("|")
        if (key !== this.backgroundArtwork.key) {
            const composites: ProjectedComposite[] = [], ceilings: RoofArtwork[] = []
            for (const floor of this.floors.values()) {
                const level = floor.provider?.twoStoreyHouse?.floor
                if (!active || !floor.root || level >= active.floor) continue
                const root = floor.root
                floor.map?.getCompositeSprites?.().forEach(source => composites.push({ source, projection: root, level }))
                if (level > 0) floor.map?.getCeilingArtwork?.().forEach(({ owner, bounds, upright }) => ceilings.push({ owner, level, layer: root, upright,
                    bounds: bounds.map(rect => new Rectangle(rect.x + root.x, rect.y + root.y, rect.width, rect.height)) }))
            }
            this.backgroundArtwork = { key, composites, ceilings }
        }
        return this.backgroundArtwork
    }

    constructor(private surface: Container, private createMap: (root: Container, sky: Container) => SceneMap,
        private createEntity: (entity: Entity, provider?: TileMapMetaProvider) => ClientRenderable,
        private retainEntity?: (entity: Entity, sprite: ClientRenderable) => boolean,
        private getRole: (id: string) => EmberRosterRole | null = () => null) {}

    clear(): void {
        this.floors.forEach(floor => floor.clear())
        this.floors.clear()
        this.backgroundArtwork = { key: "", composites: [], ceilings: [] }
    }

    floorProvider(roomId: string): TileMapMetaProvider | undefined {
        const floor = this.floors.get(roomId)
        return floor?.map && floor.provider?.roomId === roomId ? floor.provider : undefined
    }

    /** The sprites drawing one tile of a floor on screen (see MapRenderingManager.getTileArt). */
    tileArt(roomId: string, x: number, y: number, layerId: string): Sprite[] {
        const floor = this.floors.get(roomId)
        return floor?.root?.visible ? floor.map?.getTileArt?.(x, y, layerId) ?? [] : []
    }

    floorMap(roomId: string): SceneMap | undefined {
        const floor = this.floors.get(roomId)
        return floor?.provider?.roomId === roomId ? floor.map : undefined
    }

    /**
     * The player changed level to `destinationId`, a floor drawn here, from `leaving`, drawn by the
     * active map. `swap` hands the destination's map the active role and gives the active map the new
     * root made here for the floor being left; nothing is repainted. The destination's bodies are kept
     * for the active entity manager, as activateRoom does.
     */
    exchange(destinationId: string, leaving: TileMapMetaProvider, active: SceneMap,
        swap: (root: Container, sky: Container) => void): boolean {
        const destination = this.floors.get(destinationId)
        if (!destination?.map || !leaving.roomId) return false
        // A late snapshot can leave an entry for the active room; it never held this floor's drawing.
        this.floors.get(leaving.roomId)?.clear()
        this.floors.delete(leaving.roomId)
        const floor = new SceneFloorRenderer(this.surface, this.createMap, this.createEntity, false, this.retainEntity, this.getRole)
        const sky = floor.createRoot(leaving.twoStoreyHouse?.floor === 0)
        swap(floor.root, sky)
        floor.adopt(leaving, active)
        destination.releaseMap()
        destination.clear()
        this.floors.delete(destinationId)
        this.floors.set(leaving.roomId, floor)
        return true
    }

    activateRoom(roomId: string): void {
        // This floor will now be drawn by the active entity manager. Retire its
        // passive map while handing its loaded bodies to that manager.
        this.floors.get(roomId)?.clear()
        this.floors.delete(roomId)
    }

    getEntity(id: string): Entity | undefined {
        for (const floor of this.floors.values()) {
            const entity = floor.getEntity(id)
            if (entity) return entity
        }
    }

    handoffMount(id: string, destinationRoomId: string): boolean {
        for (const floor of this.floors.values()) if (floor.handoffMount(id, destinationRoomId)) return true
        return false
    }

    getRenderable(id: string): ClientRenderable | undefined {
        for (const floor of this.floors.values()) {
            const sprite = floor.getRenderable(id)
            if (sprite) return sprite
        }
    }

    projectileAim(point: Coordinate, active?: TwoStoreyHouse): CrossFloorAim | undefined {
        if (!point || !active) return
        // Prefer the highest drawn storey when bodies from different floors overlap.
        let aim: CrossFloorAim | undefined, highest = -1
        for (const floor of this.floors.values()) {
            const candidate = floor.projectileAim(point, active)
            if (candidate && floor.provider.twoStoreyHouse.floor > highest) {
                aim = candidate
                highest = floor.provider.twoStoreyHouse.floor
            }
        }
        return aim
    }

    /** Empty ground can receive a descending shot too; pick the highest visible walking surface. */
    surfaceAim(point: Coordinate, active?: TwoStoreyHouse): CrossFloorAim | undefined {
        if (!point || !active || active.floor <= 0) return
        const x = Math.floor(point.x / TileSize), y = Math.floor(point.y / TileSize)
        if (active.floorCells.some(cell => cell.x === x && cell.y === y)) return
        let selected: CrossFloorAim | undefined, highest = -1
        for (const floor of this.floors.values()) {
            const provider = floor.provider, root = floor.root
            const level = provider?.twoStoreyHouse?.floor
            if (!root?.visible || root.alpha <= .15 || level === undefined || level >= active.floor || level <= highest) continue
            const local = { x: point.x, y: point.y - root.y }
            const cell = { x: Math.floor(local.x / TileSize), y: Math.floor(local.y / TileSize) }
            if (cell.x < 0 || cell.y < 0 || cell.x >= provider.width() || cell.y >= provider.height()) continue
            const tile = provider.resolveTileMapFromLayerId("map2")?.[cell.x]?.[cell.y]
            if (level > 0 && (!tile || tile === "0" || provider.isFloorOpeningAt(cell.x, cell.y))) continue
            if (provider.isUnderRoofAt({ x: local.x, y: local.y - BODY_GROUND_OFFSET })) continue
            selected = { roomId: provider.roomId, ...local, surface: true }
            highest = level
        }
        return selected
    }

    tileEffect(effect: TileEffect): void {
        if (effect.roomId) this.floors.get(effect.roomId)?.tileEffect(effect)
    }

    /** The other storeys on screen now, each where it is drawn, so a press can pick their tiles. */
    drawnStoreys(active?: TwoStoreyHouse): DrawnStorey[] {
        const storeys: DrawnStorey[] = []
        if (!active) return storeys
        this.floors.forEach((floor, roomId) => {
            const level = floor.provider?.twoStoreyHouse?.floor
            if (!floor.root?.visible || floor.root.alpha <= .15 || level === undefined || level === active.floor) return
            storeys.push({ roomId, level, yOffset: floor.root.y, map: floor.provider,
                roofVisible: floor.map.isRoofVisibleForPlacement?.() ?? true })
        })
        return storeys
    }

    getLights(): Light[] {
        const lights: Light[] = []
        this.floors.forEach(floor => {
            if (!floor.root?.visible || floor.root.alpha <= 0) return
            floor.getLights().iterate(light => lights.push({ ...light,
                location: { x: light.location.x, y: light.location.y + floor.root.y },
                intensity: light.intensity * floor.root.alpha }))
        })
        return lights
    }

    update(frames: SceneRoomState[], active?: TwoStoreyHouse): void {
        if (!active || !frames.length) { this.clear(); return }
        const allowed = active.floorRoomIds ?? [active.groundRoomId, active.upperRoomId]
        this.floors.forEach((floor, id) => {
            if (allowed.includes(id)) return
            floor.clear()
            this.floors.delete(id)
        })
        for (const frame of frames) {
            let floor = this.floors.get(frame.roomId)
            if (!floor) {
                floor = new SceneFloorRenderer(this.surface, this.createMap, this.createEntity, false, this.retainEntity, this.getRole)
                this.floors.set(frame.roomId, floor)
            }
            floor.update([frame], active)
        }
        this.stackStoreys(active)
    }

    /**
     * A storey above the player draws over the storey it stands on. Each sorts at its own southern edge, so
     * trees in front of it can draw over it, but a storey can hold several structures: a castle's third
     * storey carries all four tower tops, so its edge is the castle's south wall, twenty rows south of the
     * north-east tower's, and it was drawn over the six storeys stacked on that tower, which then showed the
     * inside of its third storey. Raise any storey that would sort below the one under it to just above it.
     */
    private stackStoreys(active?: TwoStoreyHouse): void {
        if (!active) return
        const above = [...this.floors.values()].filter(floor => floor.root && floor.provider &&
            floor.provider.twoStoreyHouse.floor > active.floor)
        above.sort((a, b) => a.provider.twoStoreyHouse.floor - b.provider.twoStoreyHouse.floor)
        let below = -Infinity
        for (const floor of above) {
            if (floor.root.zIndex <= below) floor.root.zIndex = below + STOREY_STACK_STEP
            below = floor.root.zIndex
        }
    }

    hasForegroundFloor(playerZ: number): boolean {
        for (const floor of this.floors.values()) {
            if (this.drawsOver(floor, playerZ)) return true
        }
        return false
    }

    /** Bounds are in screen space, like the drawn player frame. Lower floors cannot cover it. */
    occludes(bounds: Rectangle, playerZ: number): boolean {
        for (const floor of this.floors.values()) {
            if (this.drawsOver(floor, playerZ) && floor.map?.overlapsRenderedMap?.(bounds)) return true
        }
        return false
    }

    private coverRuns: number[] = []

    /** Whether the storeys over the player, between them, leave none of `bounds` (screen space) showing. */
    covers(bounds: Rectangle, playerZ: number): boolean {
        const runs = this.coverRuns
        runs.length = 0
        // Floor tiles are indexed by cell and nearly always settle it under a storey; the walls and other
        // sprites are measured one by one, so only when the tiles leave part of the body showing.
        for (const floor of this.floors.values()) {
            if (this.drawsOver(floor, playerZ)) floor.map?.collectRenderedTiles?.(bounds, runs)
        }
        if (rectsCover(bounds, runs)) return true
        for (const floor of this.floors.values()) {
            if (this.drawsOver(floor, playerZ)) floor.map?.collectRenderedSprites?.(bounds, runs)
        }
        return rectsCover(bounds, runs)
    }

    private drawsOver(floor: SceneFloorRenderer, playerZ: number): boolean {
        return !!floor.root?.visible && floor.root.alpha > 0.15 && floor.root.zIndex > playerZ && !floor.cutting
    }

    linkFloorSupport(active: TileMapMetaProvider): void {
        const providers = new Map<string, TileMapMetaProvider>([[active.roomId, active]])
        this.floors.forEach((floor, id) => { if (floor.provider) providers.set(id, floor.provider) })
        providers.forEach(provider => {
            const house = provider.twoStoreyHouse
            if (!house) return
            provider.setUpperFloorProvider(providers.get(house.floorRoomIds?.[house.floor + 1] ??
                (house.floor === 0 ? house.upperRoomId : undefined)))
            if (house.floor <= 0) return
            const below = house.floorRoomIds?.[house.floor - 1] ?? house.groundRoomId
            provider.setFloorSupportProvider(providers.get(below))
        })
    }

    /** `prepaint`: also draw the storeys never drawn yet, though hidden (SceneFloorRenderer.render). */
    render(active: TwoStoreyHouse | undefined, player: Entity, prepaint = false): void {
        const cover = active && player?.location && !isExteriorFloorFall(player.floorFall)
            ? coverAt(active, player.location) : undefined
        this.floors.forEach(floor => floor.render(active, player, cover, prepaint))
        this.stackStoreys(active)
    }

    /** Whether a storey with its map in hand has never been drawn. */
    hasUnpaintedFloors(): boolean {
        for (const floor of this.floors.values()) if (floor.map?.hasPainted?.() === false) return true
        return false
    }
}
